import { List } from 'immutable'
import { loadingAsyncState, unloadedAsyncState } from 'widgets-for-react'
import {
  ctaSrc,
  ctaSrcSet,
  featuredContentSources,
  featuredContentSrc,
  linkGridSrc,
  linkGridSrcSet,
  mediaContentSrc,
  mediaContentSrcSet,
  mediaFullSrc,
  mediaFullSrcSet,
  quoteSrc,
  quoteSrcSet,
  specialistsSrc,
  specialistsSrcSet,
  textImageSrc,
  textImageSrcSet,
  expandableImageSrc,
  expandableImageSrcSet
} from '../../../../utils/imageSizes'
import { stripStyling } from '../../../../utils/richText'
import { getImageUrls } from '../../../../utils/sharepointImageUrls'
import { parseVideo } from '../../../../utils/video'
import { Block, NestedBlock, RelatedContentFilter } from '../../blocks/blocksState'
import { initialBmiBlockSTate } from '../../blocks/block/bmiBlock/bmiBlockState'
import { initialExpandBlockState } from '../../blocks/block/expandBlock/expandBlockState'
import {
  BmiBlockData,
  CarouselBlockData,
  CtaBlockData,
  CtaWithImageBlockData,
  DonationBlockData,
  DossierBlockData,
  ExpandableContentBlocksData,
  ExpandableWithImageContentBlocksData,
  FeaturedContentBlockData,
  GridBlocksCentraBlockData,
  GridBlocksImageBlockData,
  GridBlocksPlainBlockData,
  JotFormBlockData,
  LinksListBlockData,
  LocationBlockData,
  LocationExpandableBlockData,
  MediaBlockData,
  NewsTeasersBlockData,
  PageInfo,
  PeopleBlockData,
  PhoneNumbersBlockData,
  QuoteBlockData,
  QuoteWithImageBlockData,
  RelatedContentBlockData,
  SharepointBlockData,
  SpecialistsBlockData,
  TextBlockData,
  TextWithImageBlockData,
  WaitingTimesBlockData,
} from '../types/blockData'
import { initialLocationExpandableBlockState } from '../../blocks/block/locationExpandbleBlok/locationExpandableBlockState'
import { newsTeasersBlockApi } from '../../blocks/block/newsTeasersBlock/newsTeasersBlockApi'
import { peopleBlockApi } from '../../blocks/block/peopleBlock/peopleBlockApi'
import { phoneNumbersBlockApi } from '../../blocks/block/phoneNumbersBlock/phoneNumbersBlockApi'
import { relatedContentBlockApi } from '../../blocks/block/relatedContentBlock/relatedContentBlockApi'
import { specialistsBlockApi } from '../../blocks/block/specialistsBlock/specialistsBlockApi'
import { initialWaitingTimesBlockState, WaitingFor } from '../../blocks/block/waitingTimesBlock/waitingTimesBlockState'
import { hasLookupValue } from '../../../../utils/splookup'
import { initialJotFormsBlockState } from '../../blocks/block/jotFormsBlock/jotform.state'

export const transformSharepointBlockData = (
  block: SharepointBlockData,
  index: number,
  pageInfo?: PageInfo
): Block | undefined => {
  switch (block.kind) {
    case 'featured_content':
      return featuredContentBlock(block, index)
    case 'text_with_image':
      return textImageBlock(block, index)
    case 'text':
      return textBlock(block, index)
    case 'cta':
      return ctaBlock(block, index)
    case 'cta_with_image':
      return ctaImageBlock(block, index)
    case 'links_list':
      return linkListBlock(block, index)
    case 'quote':
      return quoteBlock(block, index)
    case 'quote_with_image':
      return quoteBlockImage(block, index)
    case 'media':
      return mediaBlock(block, index)
    case 'expandable_content_blocks':
      return expandBlock(block, index)
    case 'expandable_content_with_image_blocks':
        return expandBlock(block, index)
    case 'carousel':
        return carouselBlock(block, index)
    case 'grid_blocks_plain':
      return linkGridBlockPlain(block, index)
    case 'grid_blocks_image':
      return linkGridBlockImage(block, index)
    case 'grid_blocks_centra':
      return linkGridBlockCentra(block, index)
    case 'specialist':
      return specialistsBlock(block, index, pageInfo)
    case 'newsTeaser':
      return newsTeasersBlock(block, index)
    case 'phoneLookup':
      return phoneNumbersBlock(block, index)
    case 'relatedContent':
      return relatedContentBlock(block, index)
    case 'location':
      return locationBlock(block, index)
    case 'people':
      return peopleBlock(block, index)
    case 'locationExpandable':
      return locationExpandableBlock(block, index)
    case 'bmi':
      return bmiBlock(block, index)
    case 'waitingTimes':
      if (pageInfo?.department) {
        return waitingTimesBlock(block, index, { forPage: "department", title: pageInfo.department })
      } else if (pageInfo?.centrum) {
        return waitingTimesBlock(block, index, { forPage: "centrum", title: pageInfo.centrum })
      } else {
        return waitingTimesBlock(block, index)
      }
    case 'header': return undefined
    case 'jotform':
      return JotformsBlock(block, index)
    case 'dossierBlock':
      return DossierBlock(block, index)
    default:
      console.warn("unsupported block", (block as any).kind)
      return undefined
  }
}

const featuredContentBlock = (block: FeaturedContentBlockData, index: number): Block => ({
  kind: 'featuredContentBlock',
  index,
  imageLocation: block.image_position,
  image: {
    alt: block.image.alt,
    src: featuredContentSrc,
    pictureSources: featuredContentSources,
    sizes: '100vw',
    lazyload: true,
    width: '1920',
    height: '640',
    urls: getImageUrls(block.image.url),
  },
  label: block.label,
  title: block.title,
  intro: block.introduction,
  primaryAction: {
    title: block.link.title,
    url: block.link.url,
    SVGIcon: {
      width: '20px',
      height: '20px',
    },
  },
  inview: {
    threshold: 0.1,
    triggerOnce: true,
  },
})

const textImageBlock = (block: TextWithImageBlockData, index: number): Block => ({
  kind: 'textImageBlock',
  index,
  image: {
    alt: block.image.alt,
    src: textImageSrc,
    srcset: textImageSrcSet,
    sizes: '(min-width: 48rem) 40vw, 100vw',
    lazyload: true,
    width: '600',
    height: '400',
    urls: getImageUrls(block.image.url),
  },
  imageLocation: block.image_position,
  label: block.label,
  title: block.title,
  text: stripStyling(block.text),
  primaryAction: {
    url: block.link.url,
    title: block.link.title,
    SVGIcon: {
      width: '20px',
      height: '20px',
    },
  },
  secondaryAction: {
    url: block.button.url,
    title: block.button.title,
    SVGIcon: {
      width: '20px',
      height: '20px',
    },
  },
  isLinkType: block.linkType,
  inview: {
    threshold: 0.1,
    triggerOnce: true,
  },
})

const textBlock = (block: TextBlockData, index: number): Block => ({
  kind: 'textBlock',
  index,
  title: block.title,
  text: stripStyling(block.text),
  primaryAction: {
    url: block.button.url,
    title: block.button.title,
    // SVGIcon: {
    //   width: '20px',
    //   height: '20px',
    // },
  },
  secondaryAction: {
    url: block.link.url,
    title: block.link.title,
    SVGIcon: {
      width: '20px',
      height: '20px',
    },
  },
  isLinkType: block.linkType,
  inview: {
    threshold: 0.1,
    triggerOnce: true,
  },
})

const ctaBlock = (block: CtaBlockData, index: number): Block => ({
  kind: 'ctaBlock',
  index,
  title: block.title,
  intro: block.intro,
  displayOption: block.display_option !== 'none' ? block.display_option : 'full',
  primaryAction: {
    url: block.primary_button.url,
    title: block.primary_button.title,
    SVGIcon: {
      width: '20px',
      height: '20px',
    },
  },
  secondaryAction: {
    url: block.secondary_button.url,
    title: block.secondary_button.title,
    SVGIcon: {
      width: '20px',
      height: '20px',
    },
  },
  isLinkType: block.primary_button.link_type,
  inview: {
    threshold: 0.1,
    triggerOnce: true,
  },
})

const ctaImageBlock = (block: CtaWithImageBlockData, index: number): Block => ({
  kind: 'ctaBlock',
  index,
  title: block.title,
  intro: block.intro,
  displayOption: block.display_option !== 'none' ? block.display_option : 'full',
  image: {
    alt: block.image.alt,
    src: ctaSrc,
    srcset: ctaSrcSet,
    sizes: "(min-width: 60rem) 400px, 100vw",
    lazyload: true,
    width: '400',
    height: '400',
    urls: getImageUrls(block.image.url),
  },
  primaryAction: {
    url: block.primary_button.url,
    title: block.primary_button.title,
    SVGIcon: {
      width: '20px',
      height: '20px',
    },
  },
  secondaryAction: {
    url: block.secondary_button.url,
    title: block.secondary_button.title,
    SVGIcon: {
      width: '20px',
      height: '20px',
    },
  },
  isLinkType: block.primary_button.link_type,
  inview: {
    threshold: 0.1,
    triggerOnce: true,
  },
})

const quoteBlock = (block: QuoteBlockData, index: number): Block => ({
  kind: 'quoteBlock',
  index,
  text: block.text,
  author: block.author,
  primaryAction: {
    url: block.link.url,
    title: block.link.title,
    SVGIcon: {
      width: '20px',
      height: '20px',
    },
  },
  inview: {
    threshold: 0.1,
    triggerOnce: true,
  },
})

const quoteBlockImage = (block: QuoteWithImageBlockData, index: number): Block => ({
  kind: 'quoteBlock',
  index,
  text: block.text,
  author: block.author,
  primaryAction: {
    url: block.link.url,
    title: block.link.title,
    SVGIcon: {
      width: '20px',
      height: '20px',
    },
  },
  image: {
    alt: block.image.alt,
    src: quoteSrc,
    srcset: quoteSrcSet,
    sizes: 'auto',
    lazyload: true,
    width: '300',
    height: '300',
    urls: getImageUrls(block.image.url),
    placeholder: '/images/placeholder--person.svg'
  },
  inview: {
    threshold: 0.1,
    triggerOnce: true,
  },
})

export const expandBlock = (block: ExpandableContentBlocksData  | ExpandableWithImageContentBlocksData, index: number): Block => ({
  kind: block.kind === 'expandable_content_blocks' ? 'expandBlock' : 'expandBlockWithImage',
  index,
  title: block.title,
  intro: block.intro,
  image: block.kind === 'expandable_content_with_image_blocks' ? {
    alt: block.image.alt,
    src: expandableImageSrc,
    srcset: expandableImageSrcSet,
    sizes: 'auto',
    lazyload: true,
    width: '400',
    height: '400',
    urls: getImageUrls(block.image.url),
    placeholder: '/images/placeholder--person.svg'
  } : undefined,
  displayGrid: block.kind === "expandable_content_blocks" ? block.displayGrid : false,
  items: List(block.items.map((item) => ({
    isOpen: false,
    index: index,
    title: item.title,
    subtitle: item.subtitle,
    text: stripStyling(item.text),
    open: false,
    useSVGIcons: true,
    nestedBlocks: List(item.blocks)
      .map<NestedBlock>((b, i) =>
        b.kind === 'text'
          ? {
            kind: 'textBlockNested',
            index: i,
            text: stripStyling(b.text),
            inview: {
              threshold: 0.1,
              triggerOnce: true,
            },
          }
          : b.kind === 'media'
            ? {
              kind: 'mediaBlockNested',
              index: i,
              items: b.items.map((item) => ({
                kind: item.type,
                display: item.displayOption || 'content-width',
                caption: item.caption,
                image: {
                  alt: item.image.alt,
                  src: item.displayOption
                    ? item.displayOption === 'content-width'
                      ? mediaContentSrc
                      : mediaFullSrc
                    : mediaContentSrc,
                  width: item.displayOption === 'content-width' ? 'auto' : '800',
                  height: 'auto',
                  srcset: item.displayOption
                    ? item.displayOption === 'content-width'
                      ? mediaContentSrcSet
                      : mediaFullSrcSet
                    : mediaContentSrcSet,
                  sizes: '(min-width: 64rem) 800px, 100vw"',
                  lazyload: true,
                  urls: getImageUrls(item.image.url),
                },
                video: parseVideo(item.videoUrl),
              })),
              inview: {
                threshold: 0.1,
                triggerOnce: true,
              },
            }
          : b.kind === 'quote'
          ? {
              kind: 'quoteBlockNested',
              index,
              text: b.text,
              author: b.author,
              inview: {
                threshold: 0.1,
                triggerOnce: true,
              },
            }
          : {
              kind: 'linkBlockNested',
              isInView: false,
              weight: '0',
              index: i,
              items: List(b.items.map((link) => ({
                url: link.link.url,
                title: link.link.title,
                text: link.description,
                label: link.label,
                itemTitle: link.title
              }))),
              SVGIcon: {
                width: '20px',
                height: '20px',
              },
              inview: {
                threshold: 0.1,
                triggerOnce: true,
              },
            }
      )
      .toList(),
  }))),
  primaryAction: {
    url: block.link.url,
    title: block.link.title,
    SVGIcon: {
      width: '20px',
      height: '20px',
    },
  },
  inview: {
    threshold: 0.1,
    triggerOnce: true,
  },
  expandBlockState: initialExpandBlockState(block.items.length),
})

const linkListBlock = (block: LinksListBlockData, index: number): Block => ({
  kind: 'linkBlock',
  index,
  title: block.title,
  intro: block.introduction,
  items: List(block.items.map((link) => ({
    url: fixDocumentLink(link.link.url),
    title: link.link.title,
    text: link.description
  }))),
  SVGIcon: {
    width: block.title.toLowerCase().includes('folders') ? '24px' : '20px',
    height: block.title.toLowerCase().includes('folders') ? '24px' : '20px',
  },
  inview: {
    threshold: 0.1,
    triggerOnce: true,
  },
})

const linkGridBlockPlain = (block: GridBlocksPlainBlockData, index: number): Block => ({
  kind: 'linkGridBlock',
  index,
  withImages: false,
  title: block.title,
  intro: block.intro,
  items: block.items,
  itemsSVGIcon: {
    width: '20px',
    height: '20px',
  },
  primaryAction: {
    url: block.link.url,
    title: block.link.title,
    SVGIcon: {
      width: '20px',
      height: '20px',
    },
  },
  inview: {
    threshold: 0.1,
    triggerOnce: true,
  },
})

const linkGridBlockCentra = (block: GridBlocksCentraBlockData, index: number): Block => ({
  kind: 'linkGridCentraBlock',
  index,
  withImages: true,
  title: block.title,
  intro: block.intro,
  items: [block.item1, block.item2, block.item3].map((item) => ({
    ...item,
    image: item.image
      ? {
        urls: getImageUrls(item.image.url),
        alt: item.image.alt ? item.image.alt : '',
      }
      : undefined,
  })),
  itemsSVGIcon: {
    width: '20px',
    height: '20px',
  },
  imageOptions: {
    src: linkGridSrc,
    srcset: linkGridSrcSet,
    sizes: '(min-width: 81.5rem) 392px, (min-width: 64rem) 30vw, (min-width: 48rem) 50vw, 100vw"',
    lazyload: true,
    width: '392',
    height: '220',
  },
  primaryAction: {
    url: block.link.url,
    title: block.link.title,
    SVGIcon: {
      width: '20px',
      height: '20px',
    },
  },
  inview: {
    threshold: 0.1,
    triggerOnce: true,
  },
})

const carouselBlock= (block: CarouselBlockData, index: number): Block => ({
  kind: 'carouselBlock',
  index,
  title: block.title,
  intro: block.intro,
  items: block.items.map((item) => ({
    ...item,
    image: item.image
      ? {
        urls: getImageUrls(item.image.url),
        alt: item.image.alt ? item.image.alt : '',
        src: linkGridSrc,
        srcset: linkGridSrcSet,
        sizes: '(min-width: 81.5rem) 392px, (min-width: 64rem) 30vw, (min-width: 48rem) 50vw, 100vw"',
        lazyload: true,
        width: '392',
        height: '220',

      }
      : undefined,
  })),
  inview: {
    threshold: 0.1,
    triggerOnce: true,
  },
})

const linkGridBlockImage = (block: GridBlocksImageBlockData, index: number): Block => ({
  kind: 'linkGridBlock',
  index,
  withImages: true,
  title: block.title,
  intro: block.intro,
  items: block.items.map((item) => ({
    ...item,
    image: item.image
      ? {
        urls: getImageUrls(item.image.url),
        alt: item.image.alt ? item.image.alt : '',
      }
      : undefined,
  })),
  itemsSVGIcon: {
    width: '20px',
    height: '20px',
  },
  imageOptions: {
    src: linkGridSrc,
    srcset: linkGridSrcSet,
    sizes: '(min-width: 81.5rem) 392px, (min-width: 64rem) 30vw, (min-width: 48rem) 50vw, 100vw"',
    lazyload: true,
    width: '392',
    height: '220',
  },
  primaryAction: {
    url: block.link.url,
    title: block.link.title,
    SVGIcon: {
      width: '20px',
      height: '20px',
    },
  },
  inview: {
    threshold: 0.1,
    triggerOnce: true,
  },
})

const mediaBlock = (block: MediaBlockData, index: number): Block => ({
  kind: 'mediaBlock',
  index,
  title: block.title,
  intro: block.intro,
  items: block.items.map((item) => ({
    ...item,
    kind: item.type,
    display: item.displayOption || 'content-width',
    caption: item.caption,
    image:
      item.type === 'image'
        ? {
          alt: item.image.alt,
          src: item.displayOption
            ? item.displayOption === 'content-width'
              ? mediaContentSrc
              : mediaFullSrc
            : mediaContentSrc,
          width: item.displayOption === 'content-width' ? 'auto' : '100%',
          height: 'auto',
          srcset: item.displayOption
            ? item.displayOption === 'content-width'
              ? mediaContentSrcSet
              : mediaFullSrcSet
            : mediaContentSrcSet,
          sizes: '(min-width: 64rem) 800px, 100vw"',
          lazyload: true,
          urls: getImageUrls(item.image.url),
        }
        : undefined,
    video: item.type === 'video' ? parseVideo(item.videoUrl) : undefined,
  })),
  inview: {
    threshold: 0.1,
    triggerOnce: true,
  },
})

const specialistsBlock = (
  block: SpecialistsBlockData,
  index: number,
  pageInfo?: PageInfo
): Block => ({
  kind: 'specialistsBlock',
  index,
  title: block.title,
  intro: block.introduction,
  specialistsBlockState: loadingAsyncState(() => specialistsBlockApi(pageInfo)),
  imageOptions: {
    src: specialistsSrc,
    srcset: specialistsSrcSet,
    sizes: 'auto',
    lazyload: true,
    width: '251',
    height: '251',
    placeholder: '/images/placeholder--person.svg',
  },
  inview: {
    threshold: 0.1,
    triggerOnce: true,
  },
})

const newsTeasersBlock = (block: NewsTeasersBlockData, index: number): Block => ({
  kind: 'newsTeasersBlock',
  index,
  title: block.title,
  newsTeasersBlockState: loadingAsyncState(() => newsTeasersBlockApi(block.lookupList.map((item) => ({
    slug: item.id,
    pageId: item.pageId,
    title: item.lookupField,
  })))),
  inview: {
    threshold: 0.1,
    triggerOnce: true,
  },
})

const phoneNumbersBlock = (block: PhoneNumbersBlockData, index: number): Block => ({
  kind: 'phoneNumbersBlock',
  index,
  size: block.size,
  descriptions: block.lookupList.map((item) => item.lookupField),
  phoneNumbersBlockState: loadingAsyncState(() => phoneNumbersBlockApi(block.lookupList.map((item) => item.id))),
  inview: {
    threshold: 0.1,
    triggerOnce: true,
  },
})

const relatedContentBlock = (block: RelatedContentBlockData, index: number): Block => {
  const filter: RelatedContentFilter =
    block.source === 'department'
      ? {
        source: 'department',
        department: block.department.lookupField,
      }
      : block.source === 'specialist'
        ? {
          source: 'specialist',
          specialist: block.specialist.lookupField,
        }
        : {
          source: 'tags',
          tags: block.tags.map((tag) => tag.lookupField),
        }

  return {
    kind: 'relatedContentBlock',
    index,
    filter: filter,
    relatedContentBlockState: loadingAsyncState(() => relatedContentBlockApi(filter)),
    inview: {
      threshold: 0.1,
      triggerOnce: true,
    },
  }
}

const locationBlock = (block: LocationBlockData, index: number): Block => ({
  kind: 'locationBlock',
  index,
  description: block.description,
  locationId: block.location.id,
  inview: {
    threshold: 0.1,
    triggerOnce: true,
  },
})

const peopleBlock = (block: PeopleBlockData, index: number): Block => ({
  kind: 'peopleBlock',
  index,
  title: block.title,
  link: block.link,
  people: List(block.items.map((item) => ({
    name: item.name,
    phoneNumberId: item.phoneNumber.id,
    email: item.email,
    image: {
      alt: item.image.alt,
      urls: getImageUrls(item.image.url),
    },
    url: item.link.url
  }))),
  imageOptions: {
    src: specialistsSrc,
    srcset: specialistsSrcSet,
    sizes: 'auto',
    lazyload: true,
    width: '253',
    height: '253',
    placeholder: '/images/placeholder--person.svg',
  },
  peopleBlockState: loadingAsyncState(() => peopleBlockApi(block.items.map((item) => item.phoneNumber.id))),
  inview: {
    threshold: 0.1,
    triggerOnce: true,
  },
})

const locationExpandableBlock = (block: LocationExpandableBlockData, index: number): Block => ({
  kind: 'locationExpandableBlock',
  index,
  title: block.title,
  intro: block.intro,
  locationExpandableBlockState: hasLookupValue(block.department)
    ? initialLocationExpandableBlockState('department', block.department.id)
    : unloadedAsyncState(),
  inview: {
    threshold: 0.1,
    triggerOnce: true,
  },
})

const bmiBlock = (block: BmiBlockData, index: number): Block => ({
  kind: 'bmiBlock',
  index,
  bmiBlockState: initialBmiBlockSTate,
  inview: {
    threshold: 0.1,
    triggerOnce: true,
  },
})

const waitingTimesBlock = (block: WaitingTimesBlockData, index: number, departmentOrCentrum?: WaitingFor): Block => ({
  kind: 'waitingTimesBlock',
  index,
  title: block.title,
  intro: block.intro,
  suffixGeneral: stripStyling(block.suffixGeneral),
  prefixGeneral: stripStyling(block.prefixGeneral),
  suffixDepartment: stripStyling(block.suffixDepartment),
  prefixDepartment: stripStyling(block.prefixDepartment),
  spoedafspraak: stripStyling(block.spoedafspraak),
  waitingTimesBlockState: initialWaitingTimesBlockState(departmentOrCentrum),
})


// #SGCD-33 - This attempts to fix content mistakes regarding uncapitalized department names in folder urls
const documentPattern = /\/folders\?((?:department|subDepartment|centrum|subcentrum)=(?:[a-z]+[ a-zA-Z]*))?/
function fixDocumentLink(url: string): string {
  const m = documentPattern.exec(url)
  if (!m || !m[1]) return url

  const param = m[1]
  const [kind, value] = param.split("=")
  return `/folders?${kind}=${value[0].toUpperCase() + value.substring(1)}`
}

const JotformsBlock = (block: JotFormBlockData, index: number): Block => ({
  kind: 'jotFormBlock',
  index: index,
  title: block.title,
  intro: block.intro,
  label: block.label,
  formID: block.formID,
  formTitle: block.formTitle,
  useCookieBar: true,
  jotFormsBlockState: initialJotFormsBlockState
})

const DossierBlock = (block: DossierBlockData, index: number): Block => ({
  kind: 'dossierBlock',
  index: index,
  title: block.title,
  intro: block.text,
  image: {
    alt: block.image.alt,
    src: ctaSrc,
    srcset: ctaSrcSet,
    sizes: "(min-width: 60rem) 400px, 100vw",
    lazyload: true,
    width: '400',
    height: '400',
    urls: getImageUrls(block.image.url),
  },
  dossierName: block.lookupList.lookupField,
  dossierSlug: block.lookupList.id,
  dossierPageId: block.lookupList.pageId
})
